<template>
  <BaseLoading v-if="loading" :background="'rgba(255,255,255,0.5)'" />
  <!-- App推广组件 -->
  <AppPromotion :appData="appData" v-if="isShare && Object.keys(appData).length > 0" />

  <div :class="[{ 'isShare-container': isShare }, 'activity-container']" data-theme="light">
    
    <div :class="[{ 'activity-content-guest': isShare}, 'activity-content']">
      <template v-for="(mod, idx) in pageData.modules" :key="idx">
        <ActivityBanner v-if="mod.type === 'BANNER' && mod.banner" :bannerList="mod.banner.items" :title="mod.title" :module_ID="activityUuid" :module_type="mod.type" :carousel_interval="mod.banner.carousel_interval" :enable_carousel="mod.banner.enable_carousel" />
        <ActivityArticle v-else-if="mod.type === 'ARTICLE' && mod.article" :articleList="mod.article.items" :title="mod.title" :module_ID="activityUuid" :module_type="mod.type" />
        <ActivityPartner v-else-if="mod.type === 'USER' && mod.user" :partnerList="mod.user.items" :title="mod.title" :module_ID="activityUuid" :module_type="mod.type" />
        <ActivityDynamic v-else-if="mod.type === 'DYNAMIC' && mod.dynamic" :dynamicList="mod.dynamic.items" :title="mod.title" :module_ID="activityUuid" :module_type="mod.type" />
        <ActivityDeck v-else-if="mod.type === 'DECK' && mod.deck" :deckList="mod.deck.items" :title="mod.title" :module_ID="activityUuid" :module_type="mod.type" />
        <ActivityWishList v-else-if="mod.type === 'WISH_LIST' && mod.wish_list" :wishList="mod.wish_list.items" :title="mod.title" :module_ID="activityUuid" :module_type="mod.type" />
        <ActivityCardVersion v-else-if="mod.type === 'CARD_VERSION' && mod.card_version" :cardVersionList="mod.card_version.items" :title="mod.title" :module_ID="activityUuid" :module_type="mod.type" />
        <ActivityVideo v-else-if="mod.type === 'VIDEO' && mod.video" :videoList="mod.video.items" :title="mod.title" :module_ID="activityUuid" :module_type="mod.type" />
        
        <!-- 其他类型模块可继续扩展 -->
        <div v-else>【未实现模块: {{ mod.type }}】</div>
      </template>

      <div v-if="isShare" class="footer-surface-tip">
        <div class="surface"></div>
        <FooterAction :appData="appData" />
        <div class="_blank"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppPromotion from '@/components/AppPromotion.vue';
import ActivityBanner from '@components/activity_banner.vue';
import ActivityArticle from '@components/activity_article.vue';
import ActivityPartner from '@components/activity_partner.vue';
import ActivityDynamic from '@components/activity_dynamic.vue';
import ActivityDeck from '@/components/activity_deck.vue';
import ActivityCardVersion from '@/components/activity_card_version.vue';
import ActivityWishList from '@components/activity_wish_list.vue';
import ActivityVideo from '@components/activity_video.vue';
import FooterAction from '@components/FooterAction.vue';
import BaseLoading from '@/components/BaseLoading.vue';
import { usePageLoading } from '@/hooks/usePageLoading';
import { track } from '@/utils/tracking';

import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { getActivityConfig, getActivityContent } from '@/api/landing';
import { isinApp } from '@/utils/version';

// 获取路由参数
const route = useRoute();
const activityUuid = ref(route.query.uuid as string || '');
const entrance = ref(route.query.entrance as string || '');
const isShare = ref(false);
const appData = ref({})

const { loading, showLoading, hideLoading } = usePageLoading();

// 定义模块类型
type ModuleType = 'BANNER' | 'ARTICLE' | 'USER' | 'DECK' | 'CARD_VERSION' | 'WISH_LIST' | 'DYNAMIC' | 'VIDEO';

// 定义基础模块接口
interface BaseModule {
  type: ModuleType;
  title: string;
}

// 定义各种模块的接口
interface BannerModule extends BaseModule {
  type: 'BANNER';
  banner: {
    carousel_interval: number;
    enable_carousel: boolean;
    items: Array<{
      url: string;
      link?: string;
      miniprogram_link?: string;
    }>;
  };
}

interface ArticleModule extends BaseModule {
  type: 'ARTICLE';
  article: {
    items: Array<{
      type: 'IMAGE' | 'TEXT';
      image?: string;
      text?: string;
    }>;
  };
}

interface VideoModule extends BaseModule {
  type: 'VIDEO';
  video: {
    items: Array<{
      type: 'VIDEO';
      title: string;
      url: string;
      link?: string;
      miniprogram_link?: string;
      remark?: string;
    }>;
  };
}


interface UserModule extends BaseModule {
  type: 'USER';
  user: {
    items: Array<{
      user_id: string;
      avatar: string;
      username: string;
      following: boolean;
      remark: string;
      link: string;
    }>;
  };
}

interface DeckModule extends BaseModule {
  type: 'DECK';
  deck: {
    items: Array<{
      game_key: string;
      environment_id: string;
      environment_name: string;
      language: string;
      uuid: string;
      title: string;
      remark: string;
      images: string[];
      link: string;
      owner: {
        id: number;
        avatar: string;
        username: string;
      };
      nonce: string;
    }>;
  };
}

interface CardVersionModule extends BaseModule {
  type: 'CARD_VERSION';
  card_version: {
    items: Array<{
      game_key: string;
      name: string;
      image: string;
      rarity: string;
      number: string;
      link: string;
      miniprogram_link: string;
      remark: string;
    }>;
  };
}

interface WishListModule extends BaseModule {
  type: 'WISH_LIST';
  wish_list: {
    items: Array<{
      title: string;
      images: string[];
      remark: string;
      link: string;
      miniprogram_link: string;
    }>;
  };
}

interface DynamicModule extends BaseModule {
  type: 'DYNAMIC';
  dynamic: {
    items: Array<{
      id: number;
      title: string;
      link: string;
      game_key: string;
      author: {
        avatar: string;
        id: number;
        username: string;
      };
      images: string[];
      remark: string;
    }>;
  };
}

// 定义所有可能的模块类型
type Module = BannerModule | ArticleModule | UserModule | DeckModule | CardVersionModule | WishListModule | DynamicModule | VideoModule;

// 定义页面数据结构
interface PageData {
  template_key: string;
  title: string;
  sub_title: string;
  modules: Module[];
  appId?: string;
  open_app_url?: string;
  url?: string;
}

const activityClass = ref('theme-default');
const pageData = ref<PageData>({
  template_key: '',
  title: '',
  sub_title: '',
  modules: []
});


// 获取各模块数据
const fetchModuleData = async (module: any) => {
  try {
    const response = await getActivityContent(module);
    return response.data;
  } catch (error) {
    console.error('获取模块数据失败:', error);
    return { items: [] };
  }
};

// 获取营销落地页配置
const fetchActivityConfig = async (): Promise<PageData> => {
  try {
    const response = await getActivityConfig({ uuid: activityUuid.value });
    document.title = response.data.title;
    return response.data as PageData;
  } catch (error) {
    console.error('获取活动配置失败:', error);
    return {
      template_key: '',
      title: '',
      sub_title: '',
      modules: []
    };
  } finally {
    hideLoading()
  }
};

// 初始化页面数据
const initPageData = async () => {
  try {
    showLoading();
    // 获取活动配置
    const config = await fetchActivityConfig();
    
    // 更新基础配置
    pageData.value = config;

  // 设置APP数据

    appData.value = {
      appId: (config as any).appId,
      timestamp: (config as any).timestamp,
      nonceStr: (config as any).nonceStr,
      signature: (config as any).signature,
      jsApiList: (config as any).jsApiList,
      openTagList: (config as any).openTagList,
      url: (config as any).url,
      open_app_url: (config as any).open_app_url,
    };
  


    // 如果是落地页，只保留前2个模块
    // if (isShare.value) {
    //   pageData.value.modules = pageData.value.modules.slice(0, 4);
    //   hideLoading()
    //   return;
    // }


    // 根据模块类型动态获取数据
    const fetchPromises = pageData.value.modules.map(async (module) => {
      // console.log(module, '当前模块');
      switch (module.type) {
        case 'USER':
          return { module, data: await fetchModuleData(module.user) };
        case 'DECK':
          return { module, data: await fetchModuleData(module.deck) };
        case 'CARD_VERSION':
          return { module, data: await fetchModuleData(module.card_version) };
        case 'WISH_LIST':
          return { module, data: await fetchModuleData(module.wish_list) };
        case 'DYNAMIC':
          return { module, data: await fetchModuleData(module.dynamic) };
        default:
          return { module, data: null };
      }
    });

    // 等待所有数据获取完成
    const results = await Promise.all(fetchPromises);
    // console.log(pageData.value, '活动配置');

    // 更新模块数据
    pageData.value.modules = pageData.value.modules.map(module => {
      const result = results.find(r => r.module === module);
      if (!result || !result.data) return module;

      switch (module.type) {
        case 'USER':
          return { ...module, user: result.data } as UserModule;
        case 'DECK':
          return { ...module, deck: result.data } as DeckModule;
        case 'CARD_VERSION':
          return { ...module, card_version: result.data } as CardVersionModule;
        case 'WISH_LIST':
          return { ...module, wish_list: result.data } as WishListModule;
        case 'DYNAMIC':
          return { ...module, dynamic: result.data } as DynamicModule;
        default:
          return module;
      }
    });
    hideLoading();
  } catch (error) {
    console.error('初始化页面数据失败:', error);
    hideLoading();
  }
};

onMounted(async () => {
  isShare.value = !await isinApp();

  console.log("isShare", isShare.value)
  initPageData();

  track('view_H5Landing_module', {
    entrance
  });
});

onBeforeUnmount(() => {
  // 清理工作
});
</script>

<style lang="scss" scoped>
.activity-container {
  background: var(--body-gray);
  min-height: 100vh;

  &.isShare-container{
    height: 100vh;
    overflow: hidden;
  }

  .activity-content{
    padding: var(--padding-9) var(--padding-9) 0px var(--padding-9);
  }

  // 默认主题
  &.theme-default {
    .action-btn button {
      background: linear-gradient(45deg, #ff6b6b, #ff8e8e);
    }
  }

  // 其他主题样式可以在这里添加
  &.theme-blue {
    .action-btn button {
      background: linear-gradient(45deg, #4a90e2, #63b3ed);
    }
  }
}

.footer-surface-tip{
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;

  .surface{
    height: 56px;
    background: linear-gradient(180deg, var(--container-surface-white-completely-alpha) 0%, var(--container-surface-white) 100%);
  }

  :deep(.footer-action){
    background: var(--container-surface-white);
  }
  
  ._blank{
    height: 18px;
    background: var(--container-surface-white);
  }
}
</style> 